/* eslint-disable @next/next/no-img-element */
import React, { useState, useRef } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'

import { useTranslate } from 'react-polyglot'
import delve from 'dlv'
import classnames from 'classnames/bind'

import Link from 'components/shared/Link'
import Button from 'components/shared/Button'
import SearchButton from 'components/shared/search/SearchButton'
import Layout from 'components/shared/Layout'
import useSetStyle from 'hooks/useSetStyle'
import useSpring from 'hooks/useSpring'
import { Label } from 'components/typo'
import NewsletterPopIn from 'src/components/slices/NewsletterPopIn'
import { SINGLES } from 'src/services/constants'
import BlogNavDropdown from './Dropdown'

import NewsletterIcon from './newsletter-icon.svg'
import css from './styles.module.scss'
import { useProgressContext } from '../ProgressProvider'
import { useRegisterStickyBar } from '../HasStickyBarProvider'
import { ReactComponent as BlogIcon } from './BlogTag.svg'

const cx = classnames.bind(css)

const indices = [
  {
    label: 'Blog Posts',
    name: 'blog_posts',
  },
]

const BlogNav = ({ className, current, categories, newsletter }) => {
  const [isSearching, setIsSearching] = useState(false)
  const [newsletterIsOpen, setNewsletterIsOpen] = useState(false)
  const [visibleDropdownId, setVisibleDropdownId] = useState(null)

  const t = useTranslate()
  const progress = useProgressContext()
  const progressRef = useRef()

  const setProgressStyle = useSetStyle(progressRef)
  const [setSpring] = useSpring({
    config: {
      interpolation: 'basic',
      friction: 5,
    },
    progress: 0,
    onUpdate: ({ progress }) => {
      setProgressStyle({
        x: `${progress * 100}%`,
      })
    },
  })

  useRegisterStickyBar()

  useIsomorphicLayoutEffect(() => {
    setSpring({ progress })
  }, [progress])

  return (
    <div className={cx(className, css.BlogNav, { isSearching })}>
      <Layout type="wrapper" className={css.layout}>
        <div
          className={css.navStartPart}
          onMouseLeave={() => setVisibleDropdownId(null)}
        >
          <Link href={`${SINGLES.blog.url}`}>
            <BlogIcon alt="blog" className={css.blogIcon} />
          </Link>
          {categories && categories?.length > 0 && (
            <div className={css.linksWrapper}>
              <ul className={css.links}>
                <li className={css.listItem}>
                  <Link
                    className={cx(css.link, {
                      isActive: current === SINGLES.blog.url,
                    })}
                    href={`${SINGLES.blog.url}`}
                  >
                    <Label className={css.label}>
                      {t('blog.latest_articles')}
                    </Label>
                  </Link>
                </li>
                {categories.map((category, index) => {
                  const slug = delve(category, 'slug')
                  const href = `${SINGLES.blog.url}/categories/${slug}`
                  const postSubCategories =
                    category?.post_sub_categories?.data?.map(
                      ({ id, attributes }) => ({ id, ...attributes })
                    ) || []
                  const widthDropdown = postSubCategories.length > 0
                  return (
                    <li
                      key={index}
                      className={css.listItem}
                      onMouseEnter={() => {
                        setVisibleDropdownId(category.id)
                      }}
                    >
                      <Link
                        className={cx(css.link, {
                          isActive: current === slug,
                        })}
                        href={{
                          pathname: href,
                          query:
                            category.name === 'Tutorials' ? { type: 'v4' } : {},
                        }}
                      >
                        <Label className={css.label}>
                          <span>{category?.name}</span>
                          {widthDropdown && <span className={css.arrow} />}
                        </Label>
                        {widthDropdown && visibleDropdownId === category.id && (
                          <BlogNavDropdown
                            items={postSubCategories}
                            category={slug}
                            onMouseLeave={() => setVisibleDropdownId(null)}
                          />
                        )}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
        <div className={css.buttons}>
          <SearchButton
            isSearching={isSearching}
            setIsSearching={setIsSearching}
            indices={indices}
            className={css.button}
          />
          <Button
            onClick={() => {
              setNewsletterIsOpen(true)
            }}
            tagName="button"
            className={cx(css.button, { newsletterIsOpen })}
            buttonClassName={css.buttonInside}
            theme="transparent-blue-500"
            border={css.subscribeButtonBorder}
          >
            <span className={css.subscribeButton}>Subscribe</span>
            <img
              src={NewsletterIcon}
              className={css.newsletterIcon}
              alt="Newsletter Icon"
            />
          </Button>
        </div>
        {newsletterIsOpen && (
          <NewsletterPopIn
            newsletter={newsletter}
            className={css.newsletter}
            onClose={() => {
              setNewsletterIsOpen(false)
            }}
          />
        )}
      </Layout>
      <div className={css.progressBar}>
        <div ref={progressRef} className={css.progress} />
      </div>
    </div>
  )
}

BlogNav.defaultProps = {}

export default BlogNav
